import { useEffect, useMemo, useState } from "react";
import { fetchLosDetails } from "../api/Los";
import {
  MODE_B_LINEHAUL_TARIFF_NONE_ID,
  buildModeBLinehaulTariffOptions,
  injectLosOptionIfMissing,
  losRecordToTariffOption,
  normalizeLosId,
  normalizeLosOptionsList,
  resolveModeBLinehaulTariffLosId,
} from "./serviceLevelDerivation";

export const useModeBLinehaulTariffOptions = ({
  serviceLevelFlowEnabled,
  baseOptions = [],
  accountBillingConfig = {},
  orgBillingRules = {},
  savedLinehaulTariffId = null,
}) => {
  const losOptions = useMemo(
    () => normalizeLosOptionsList(baseOptions),
    [baseOptions]
  );

  const losOptionsKey = useMemo(
    () => losOptions.map((o) => `${o.id}::${o.code}::${o.rate_sheet_id || ""}`).join("|"),
    [losOptions]
  );

  const modeBDefaultTariffId = useMemo(() => {
    if (serviceLevelFlowEnabled) return null;
    return resolveModeBLinehaulTariffLosId({
      billLinehaulWith: accountBillingConfig?.bill_linehaul_with,
      linehaulLosId: accountBillingConfig?.linehaul_los_id,
      organizationLinhaulLosId: orgBillingRules?.organization_linhaul_los_id,
    });
  }, [serviceLevelFlowEnabled, accountBillingConfig, orgBillingRules]);

  const [injectedOption, setInjectedOption] = useState(null);
  const [savedInjectedOption, setSavedInjectedOption] = useState(null);
  const savedTariffId = useMemo(
    () => normalizeLosId(savedLinehaulTariffId),
    [savedLinehaulTariffId]
  );

  useEffect(() => {
    if (serviceLevelFlowEnabled || !modeBDefaultTariffId) {
      setInjectedOption(null);
      return undefined;
    }
    if (modeBDefaultTariffId === MODE_B_LINEHAUL_TARIFF_NONE_ID) {
      setInjectedOption(null);
      return undefined;
    }
    if (losOptions.some((o) => String(o.id) === String(modeBDefaultTariffId))) {
      setInjectedOption(null);
      return undefined;
    }

    let cancelled = false;
    fetchLosDetails(modeBDefaultTariffId).then((res) => {
      if (cancelled) return;
      const opt = losRecordToTariffOption(res?.los);
      setInjectedOption(
        opt || {
          id: String(modeBDefaultTariffId),
          code: String(modeBDefaultTariffId),
          name: String(modeBDefaultTariffId),
          rate_sheet_id: "",
        }
      );
    }).catch(() => {
      if (!cancelled) {
        setInjectedOption({
          id: String(modeBDefaultTariffId),
          code: String(modeBDefaultTariffId),
          name: String(modeBDefaultTariffId),
          rate_sheet_id: "",
        });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [serviceLevelFlowEnabled, modeBDefaultTariffId, losOptionsKey]);

  useEffect(() => {
    if (!savedTariffId || savedTariffId === MODE_B_LINEHAUL_TARIFF_NONE_ID) {
      setSavedInjectedOption(null);
      return undefined;
    }
    if (losOptions.some((o) => String(o.id) === savedTariffId)) {
      setSavedInjectedOption(null);
      return undefined;
    }

    let cancelled = false;
    fetchLosDetails(savedTariffId).then((res) => {
      if (cancelled) return;
      const opt = losRecordToTariffOption(res?.los);
      setSavedInjectedOption(
        opt || {
          id: savedTariffId,
          code: savedTariffId,
          name: savedTariffId,
          rate_sheet_id: "",
        }
      );
    }).catch(() => {
      if (!cancelled) {
        setSavedInjectedOption({
          id: savedTariffId,
          code: savedTariffId,
          name: savedTariffId,
          rate_sheet_id: "",
        });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [savedTariffId, losOptionsKey]);

  const effectiveLosOptions = useMemo(() => {
    let opts = buildModeBLinehaulTariffOptions(losOptions);
    if (!serviceLevelFlowEnabled && injectedOption) {
      opts = injectLosOptionIfMissing(opts, injectedOption);
    }
    if (savedInjectedOption) {
      opts = injectLosOptionIfMissing(opts, savedInjectedOption);
    }
    return opts;
  }, [serviceLevelFlowEnabled, losOptions, injectedOption, savedInjectedOption]);

  const effectiveLosOptionsKey = useMemo(
    () => effectiveLosOptions.map((o) => `${o.id}::${o.code}::${o.rate_sheet_id || ""}`).join("|"),
    [effectiveLosOptions]
  );

  const modeBDefaultReady = useMemo(() => {
    if (serviceLevelFlowEnabled || !modeBDefaultTariffId) return false;
    return effectiveLosOptions.some((o) => String(o.id) === String(modeBDefaultTariffId));
  }, [serviceLevelFlowEnabled, modeBDefaultTariffId, effectiveLosOptions]);

  return {
    effectiveLosOptions,
    modeBDefaultTariffId,
    modeBDefaultReady,
    effectiveLosOptionsKey,
  };
};
